import { useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Collapse, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import usePlan from '../hooks/usePlan';
import { isOrgAdminRole } from '../utils/roleUtils';
import FeatureNotice from './FeatureNotice';
import WhatsAppShareModal from './WhatsAppShareModal';

import DateInput from './DateInput';
import { formatDate, formatDateTime } from '../utils/dateFormat';
const num = (v) => {
  const n = parseFloat(v);
  return Number.isNaN(n) ? 0 : n;
};
const errorMessage = (error, fallback) => {
  const data = error.response?.data;
  return typeof data === 'string' && data ? data : fallback;
};
const when = (iso) => (iso ? formatDateTime(iso) : '-');

/**
 * Who owes what, split by how late it is, with payment reminders: a button to remind a customer now, a
 * customer-level opt-out, the organization's reminder rules and wording, and a log of every reminder sent.
 */
function Receivables({ active, onLedger }) {
  const { user } = useAuth();
  const { has } = usePlan();
  // Sending everything that is due at once is the organization admin's; finance reminds customers one at a time
  const canRunAll = !!user?.isSystemAdmin || (user?.roles || []).some(isOrgAdminRole);
  const [asOf, setAsOf] = useState('');
  // The customer whose statement is being sent on WhatsApp
  const [shareFor, setShareFor] = useState(null);
  const [aging, setAging] = useState(null);
  const [summary, setSummary] = useState({});
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currency, setCurrency] = useState('');
  const [reloadKey, setReloadKey] = useState(0);
  const [statement, setStatement] = useState(null);
  const [due, setDue] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [preview, setPreview] = useState(null);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    setLoading(true);
    Promise.all([
      axios.get('/api/accounts/receivables-aging', { params: asOf ? { asOf } : {} }),
      axios.get('/api/accounts/reminders/summary'),
      axios.get('/api/accounts/reminders/log', { params: { limit: 50 } })
    ])
      .then(([a, s, l]) => {
        if (cancelled) return;
        setAging(a.data);
        setSummary(s.data || {});
        setLog(Array.isArray(l.data) ? l.data : []);
      })
      .catch((error) => { if (!cancelled) toast.error(errorMessage(error, 'Error fetching receivables')); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [active, asOf, reloadKey]);

  useEffect(() => {
    if (!active || !user?.organizationId) return;
    axios.get(`/api/organizations/${user.organizationId}`)
      .then((r) => setCurrency(r.data?.defaultCurrency || ''))
      .catch(() => {});
  }, [active, user?.organizationId]);

  const money = (v) => `${currency} ${num(v).toFixed(2)}`;
  const reload = () => setReloadKey((k) => k + 1);

  const openStatement = async (row) => {
    try {
      const r = await axios.get(`/api/accounts/receivables/customers/${row.customerId}/statement`);
      setStatement({ row, data: r.data });
    } catch (error) {
      toast.error(errorMessage(error, 'Could not load the statement'));
    }
  };

  // Shows the email exactly as it would go out, and only sends after a second click
  const remind = async (row) => {
    try {
      setBusyId(row.customerId);
      const r = await axios.get(`/api/accounts/reminders/customers/${row.customerId}/preview`);
      setPreview({ row, mail: r.data });
    } catch (error) {
      toast.error(errorMessage(error, 'Could not prepare the reminder'));
    } finally {
      setBusyId(null);
    }
  };

  const sendPreview = async () => {
    const { row } = preview;
    try {
      setBusyId(row.customerId);
      await axios.post(`/api/accounts/reminders/customers/${row.customerId}/send`);
      toast.success(`Reminder sent to ${row.email}`);
      setPreview(null);
      reload();
    } catch (error) {
      toast.error(errorMessage(error, 'Could not send the reminder'));
      reload();
    } finally {
      setBusyId(null);
    }
  };

  const toggleOpt = async (row) => {
    try {
      await axios.put(`/api/accounts/reminders/customers/${row.customerId}/opt`, { enabled: !row.remindersEnabled });
      reload();
    } catch (error) {
      toast.error(errorMessage(error, 'Could not change this customer'));
    }
  };

  const showDue = async () => {
    try {
      const r = await axios.get('/api/accounts/reminders/due');
      setDue(r.data);
    } catch (error) {
      toast.error(errorMessage(error, 'Could not work out what is due'));
    }
  };

  const runNow = async () => {
    if (!window.confirm('Send every reminder that is due today now? Customers already reminded recently are skipped.')) return;
    try {
      setRunning(true);
      const r = await axios.post('/api/accounts/reminders/run');
      const d = r.data;
      toast[d.failed > 0 ? 'warning' : 'success'](d.message || `${d.sent} sent${d.failed ? `, ${d.failed} failed` : ''}`);
      setDue(null);
      reload();
    } catch (error) {
      toast.error(errorMessage(error, 'Could not send the reminders'));
    } finally {
      setRunning(false);
    }
  };

  const totals = aging?.totals;
  const overdue = totals ? num(totals.days1to30) + num(totals.days31to60) + num(totals.days61to90) + num(totals.over90) : 0;

  return (
    <div className="mt-3">
      <div className="d-flex flex-wrap gap-3 align-items-end mb-3">
        <Form.Group>
          <Form.Label className="mb-1">As of</Form.Label>
          <DateInput value={asOf} onChange={(e) => setAsOf(e.target.value)} />
        </Form.Group>
        <Button variant="outline-secondary" onClick={showDue}>What would be sent today?</Button>
        {canRunAll && (
          <Button variant="primary" onClick={runNow} disabled={running || !has("PAYMENT_REMINDERS")}>{running ? 'Sending...' : 'Send due reminders now'}</Button>
        )}
        {loading && <Spinner animation="border" size="sm" />}
      </div>

      {totals && (
        <Row className="g-3 mb-3">
          <Col md={3}><Card body><div className="text-muted small">Outstanding</div><h5 className="mb-0">{money(totals.outstanding)}</h5></Card></Col>
          <Col md={3}><Card body><div className="text-muted small">Overdue</div><h5 className="mb-0 text-danger">{money(overdue)}</h5></Card></Col>
          <Col md={3}><Card body><div className="text-muted small">Advances / credit held</div><h5 className="mb-0">{money(totals.advance)}</h5></Card></Col>
          <Col md={3}><Card body><div className="text-muted small">Customers over credit limit</div><h5 className="mb-0">{aging.customersOverLimit}</h5></Card></Col>
        </Row>
      )}

      <Card className="mb-4">
        <Card.Body>
          <Table striped bordered hover responsive size="sm">
            <thead>
              <tr>
                <th>Customer</th><th className="text-end">Not due</th><th className="text-end">1-30</th><th className="text-end">31-60</th>
                <th className="text-end">61-90</th><th className="text-end">90+</th><th className="text-end">Outstanding</th>
                <th className="text-end">Advance</th><th>Last reminder</th><th>Auto</th><th />
              </tr>
            </thead>
            <tbody>
              {(aging?.rows || []).map((r) => {
                const sum = summary[r.customerId];
                return (
                  <tr key={r.customerId}>
                    <td>
                      {r.customerName}
                      {r.overCreditLimit && <Badge bg="danger" className="ms-1">Over limit</Badge>}
                      {!r.email && <div className="small text-muted">no email</div>}
                    </td>
                    <td className="text-end">{money(r.notDue)}</td>
                    <td className="text-end">{money(r.days1to30)}</td>
                    <td className="text-end">{money(r.days31to60)}</td>
                    <td className="text-end">{money(r.days61to90)}</td>
                    <td className="text-end">{money(r.over90)}</td>
                    <td className="text-end"><strong>{money(r.outstanding)}</strong></td>
                    <td className="text-end">{num(r.advance) > 0 ? money(r.advance) : '-'}</td>
                    <td className="small">{sum ? `${when(sum.lastSentAt)} (${sum.count})` : '-'}</td>
                    <td>
                      <Form.Check type="switch" id={`opt-${r.customerId}`} checked={Boolean(r.remindersEnabled)}
                        title="Automatic reminders for this customer" onChange={() => toggleOpt(r)} />
                    </td>
                    <td className="text-nowrap">
                      <Button size="sm" variant="outline-info" className="me-1" onClick={() => openStatement(r)}>Statement</Button>
                      <Button size="sm" variant="outline-success" className="me-1" title="Send the statement of dues on WhatsApp"
                        onClick={() => setShareFor(r)}><i className="bi bi-whatsapp" /></Button>
                      <Button size="sm" variant="outline-warning" disabled={!has("PAYMENT_REMINDERS") || !r.email || num(r.outstanding) <= 0 || busyId === r.customerId}
                        onClick={() => remind(r)}>{busyId === r.customerId ? 'Sending...' : 'Remind'}</Button>
                    </td>
                  </tr>
                );
              })}
              {aging && aging.rows.length === 0 && (
                <tr><td colSpan="11" className="text-center text-muted">Nobody owes you anything{asOf ? ' on that date' : ''}.</td></tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <FeatureNotice feature="PAYMENT_REMINDERS" className="mt-3">Reminders can’t be sent or changed.</FeatureNotice>
      <ReminderSettingsCard onChanged={reload} />
      {shareFor && (
        <WhatsAppShareModal path={`statements/${shareFor.customerId}`} title={`Send statement to ${shareFor.customerName}`}
          what="statement" onHide={() => setShareFor(null)} />
      )}

      <h5 className="mt-4">Reminder log</h5>
      <Card>
        <Card.Body>
          <Table striped bordered size="sm" responsive>
            <thead>
              <tr><th>Sent</th><th>Customer</th><th>Type</th><th>To</th><th className="text-end">Bills</th><th className="text-end">Due</th><th>Result</th><th>By</th></tr>
            </thead>
            <tbody>
              {log.map((l) => (
                <tr key={l.id}>
                  <td>{when(l.sentAt)}</td>
                  <td>{l.customerName}</td>
                  <td>{l.reminderType === 'PRE_DUE' ? 'Before due' : l.reminderType === 'OVERDUE' ? 'Overdue' : 'Manual'}</td>
                  <td>{l.recipient}</td>
                  <td className="text-end">{l.billCount}</td>
                  <td className="text-end">{money(l.totalDue)}</td>
                  <td>
                    <Badge bg={l.status === 'SENT' ? 'success' : 'danger'}>{l.status === 'SENT' ? 'Sent' : 'Failed'}</Badge>
                    {l.errorMessage && <div className="small text-danger">{l.errorMessage}</div>}
                  </td>
                  <td className="small">{l.triggeredBy === 'SCHEDULER' ? 'Automatic' : l.triggeredBy}</td>
                </tr>
              ))}
              {log.length === 0 && <tr><td colSpan="8" className="text-center text-muted">No reminders sent yet.</td></tr>}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Modal show={Boolean(statement)} onHide={() => setStatement(null)} size="lg">
        <Modal.Header closeButton><Modal.Title>Statement — {statement?.row.customerName}</Modal.Title></Modal.Header>
        <Modal.Body>
          {statement && (
            <>
              <p className="text-muted small">{statement.data.from} to {statement.data.to}. Opening balance {money(statement.data.openingBalance)}.</p>
              <Table bordered size="sm">
                <thead><tr><th>Date</th><th>Document</th><th>Details</th><th className="text-end">Debit</th><th className="text-end">Credit</th><th className="text-end">Balance</th></tr></thead>
                <tbody>
                  {statement.data.lines.map((l, i) => (
                    <tr key={`${l.reference}-${i}`}>
                      <td>{formatDate(l.date)}</td><td>{l.reference}</td><td>{l.description}</td>
                      <td className="text-end">{num(l.debit) ? money(l.debit) : ''}</td>
                      <td className="text-end">{num(l.credit) ? money(l.credit) : ''}</td>
                      <td className="text-end">{money(l.balance)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <div className="d-flex justify-content-between align-items-center">
                {onLedger ? (
                  <Button size="sm" variant="outline-secondary" onClick={() => { const id = statement.row.customerId; setStatement(null); onLedger(id); }}>
                    Ledger statement (download, print)
                  </Button>
                ) : <span />}
                <strong>Closing balance {money(statement.data.closingBalance)}</strong>
              </div>
            </>
          )}
        </Modal.Body>
      </Modal>

      <Modal show={Boolean(preview)} onHide={() => setPreview(null)} size="lg">
        <Modal.Header closeButton><Modal.Title>Send a reminder to {preview?.row.customerName}</Modal.Title></Modal.Header>
        <Modal.Body>
          {preview && (
            <>
              <p className="small text-muted mb-2">
                From <strong>{preview.mail.senderName}</strong> &middot; to <strong>{preview.mail.to}</strong> &middot; replies go to <strong>{preview.mail.replyTo || 'nobody (no reply address set)'}</strong>
              </p>
              <p><strong>{preview.mail.subject}</strong></p>
              {preview.mail.html ? (
                <iframe
                  title="Reminder preview"
                  sandbox=""
                  srcDoc={preview.mail.html}
                  className="w-100 border rounded"
                  style={{ height: 460, background: '#f3f4f6' }}
                />
              ) : (
                <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>{preview.mail.body}</pre>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setPreview(null)}>Cancel</Button>
          <Button variant="warning" onClick={sendPreview} disabled={busyId !== null}>{busyId !== null ? 'Sending...' : 'Send reminder'}</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={Boolean(due)} onHide={() => setDue(null)} size="lg">
        <Modal.Header closeButton><Modal.Title>Due a reminder today</Modal.Title></Modal.Header>
        <Modal.Body>
          {due && due.length === 0 && <p className="text-muted mb-0">Nothing is due a reminder today under your current rules.</p>}
          {due && due.map((c) => (
            <Card key={c.customerId} className="mb-2">
              <Card.Body className="py-2">
                <div className="d-flex justify-content-between">
                  <strong>{c.customerName} <span className="text-muted small">{c.email}</span></strong>
                  <span>{c.type === 'OVERDUE' ? <Badge bg="danger">Overdue</Badge> : <Badge bg="info">Before due</Badge>} {money(c.total)}</span>
                </div>
                <div className="small text-muted">
                  {c.bills.map((b) => `${b.billNumber} (${b.daysOverdue > 0 ? `${b.daysOverdue}d overdue` : `due ${formatDate(b.dueDate)}`})`).join(', ')}
                </div>
              </Card.Body>
            </Card>
          ))}
          <p className="small text-muted mt-2 mb-0">Reminders only go out if they are switched on below. This list is what the daily run would send today.</p>
        </Modal.Body>
      </Modal>
    </div>
  );
}

/** The organization's reminder rules and wording. */
function ReminderSettingsCard({ onChanged }) {
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get('/api/accounts/reminders/settings')
      .then((r) => {
        setLoaded(r.data);
        const s = r.data.settings;
        setForm({
          enabled: Boolean(s.enabled), daysBeforeDue: s.daysBeforeDue ?? 3, overdueCadenceDays: s.overdueCadenceDays ?? 7,
          maxRemindersPerBill: s.maxRemindersPerBill ?? 3, replyToEmail: s.replyToEmail || '',
          preDueSubject: s.preDueSubject || '', preDueBody: s.preDueBody || '', overdueSubject: s.overdueSubject || '', overdueBody: s.overdueBody || ''
        });
      })
      .catch(() => {});
  }, []);

  if (!form || !loaded) return null;
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const save = async () => {
    try {
      setSaving(true);
      await axios.put('/api/accounts/reminders/settings', {
        ...form,
        daysBeforeDue: Number(form.daysBeforeDue), overdueCadenceDays: Number(form.overdueCadenceDays), maxRemindersPerBill: Number(form.maxRemindersPerBill)
      });
      toast.success('Reminder settings saved');
      onChanged();
    } catch (error) {
      toast.error(errorMessage(error, 'Could not save the settings'));
    } finally {
      setSaving(false);
    }
  };

  const d = loaded.defaults;
  return (
    <Card className="mb-3">
      <Card.Header className="d-flex justify-content-between align-items-center" style={{ cursor: 'pointer' }} onClick={() => setOpen(!open)}>
        <h5 className="mb-0">Payment reminder settings {form.enabled ? <Badge bg="success">On</Badge> : <Badge bg="secondary">Off</Badge>}</h5>
        <span className="text-muted small">{open ? 'Hide' : 'Show'}</span>
      </Card.Header>
      <Collapse in={open}>
        <div>
          <Card.Body>
            {!loaded.mailConfigured && (
              <Alert variant="warning" className="py-2 small">Email isn&apos;t configured on this server yet, so no reminder can be sent. A system administrator sets this up under System Admin &gt; Email.</Alert>
            )}
            <Form.Check type="switch" id="rem-enabled" label="Send payment reminders by email automatically (every morning)" checked={form.enabled}
              onChange={(e) => set({ enabled: e.target.checked })} className="mb-3" />
            <Row className="g-3 mb-3">
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Remind this many days before due</Form.Label>
                  <Form.Control type="number" min="0" max="30" value={form.daysBeforeDue} onChange={(e) => set({ daysBeforeDue: e.target.value })} />
                  <Form.Text className="text-muted">0 = no reminder before the due date</Form.Text>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Repeat every (days) once overdue</Form.Label>
                  <Form.Control type="number" min="1" max="90" value={form.overdueCadenceDays} onChange={(e) => set({ overdueCadenceDays: e.target.value })} />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Most reminders per bill</Form.Label>
                  <Form.Control type="number" min="1" max="12" value={form.maxRemindersPerBill} onChange={(e) => set({ maxRemindersPerBill: e.target.value })} />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Replies go to</Form.Label>
                  <Form.Control type="email" value={form.replyToEmail} placeholder="your organization email" onChange={(e) => set({ replyToEmail: e.target.value })} />
                </Form.Group>
              </Col>
            </Row>
            <p className="small text-muted">
              Emails are sent from iBigDo&apos;s address under your organization&apos;s name, and replies come to you. You can use
              {' '}{loaded.placeholders.map((p) => `{{${p}}}`).join(', ')} in the wording. Leave a field blank to use the standard text.
            </p>
            <p className="small text-muted">
              The email is formatted, headed by your organization&apos;s logo (or its name), with a plain-text copy for mail apps that
              can&apos;t show formatting. For emphasis, write <code>**bold**</code>; a line starting <code>- </code> becomes a bullet.
            </p>
            <Row className="g-3">
              <Col md={6}>
                <Form.Group className="mb-2">
                  <Form.Label>Before-due subject</Form.Label>
                  <Form.Control value={form.preDueSubject} placeholder={d.preDueSubject} onChange={(e) => set({ preDueSubject: e.target.value })} />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Before-due message</Form.Label>
                  <Form.Control as="textarea" rows={7} value={form.preDueBody} placeholder={d.preDueBody} onChange={(e) => set({ preDueBody: e.target.value })} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-2">
                  <Form.Label>Overdue subject</Form.Label>
                  <Form.Control value={form.overdueSubject} placeholder={d.overdueSubject} onChange={(e) => set({ overdueSubject: e.target.value })} />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Overdue message</Form.Label>
                  <Form.Control as="textarea" rows={7} value={form.overdueBody} placeholder={d.overdueBody} onChange={(e) => set({ overdueBody: e.target.value })} />
                </Form.Group>
              </Col>
            </Row>
            <div className="mt-3">
              <Button variant="primary" onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save settings'}</Button>
            </div>
          </Card.Body>
        </div>
      </Collapse>
    </Card>
  );
}

export default Receivables;
